import { Flex, Grid, Image } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import { setReviewCreationKey, useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';

import MeshLogoImage from 'assets/assetsV1/icons/specialIcons/meshLogo.svg';
import EventTypeImage from 'assets/assetsV1/illustrations/deerIllustrations/event_type.svg';
// import RecurringTagImage from 'assets/assetsV1/illustrations/deerIllustrations/recurring.svg';
import AnnualReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/annualReview.png';
// import CompetencyOnlyReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/competencyOnlyReview.png';
import CustomReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/customReview.png';
import PipReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/pipReview.png';
import ProbationReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/probationReview.png';
import QuarterlyReviewImage from 'assets/assetsV1/illustrations/deerIllustrations/reviewPresetTypes/quarterlyReview.png';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { RadioCard } from 'uiToolkitV1/RadioCard/RadioCard';
import { TextLabel, TextLabelSmall, TextBodyMedium } from 'uiToolkitV1/Typography';

import { ReviewProcessV2PresetsFooter } from './ReviewProcessV2PresetsFooter';

export const REVIEW_PRESET_TYPES = {
  ANNUAL_APPRAISAL: 'ANNUAL_APPRAISAL',
  QUARTERLY_REVIEW: 'QUARTERLY_REVIEW',
  PIP_REVIEW: 'PIP_REVIEW',
  PROBATION_REVIEW: 'PROBATION_REVIEW',
  // COMPETENCY_ASSESSMENT: 'COMPETENCY_ASSESSMENT',
  CUSTOM: 'CUSTOM',
};

export const REVIEW_PRESETS = [
  {
    type: REVIEW_PRESET_TYPES.ANNUAL_APPRAISAL,
    title: 'Annual Review',
    subText:
      'A comprehensive year-end evaluation assessing overall performance, competencies, achievements, challenges, and development areas to support growth.',
    imgSrc: AnnualReviewImage,
  },
  {
    type: REVIEW_PRESET_TYPES.QUARTERLY_REVIEW,
    title: 'Quarterly Review',
    subText: 'A lightweight, frequent review to track progress and ensure continuous feedback and alignment on goals.',
    imgSrc: QuarterlyReviewImage,
  },
  {
    type: REVIEW_PRESET_TYPES.PIP_REVIEW,
    title: 'PIP Review',
    subText:
      'A structured review for employees on a Performance Improvement Plan to track progress against the decided action plan.',
    imgSrc: PipReviewImage,
  },
  {
    type: REVIEW_PRESET_TYPES.PROBATION_REVIEW,
    title: 'Probation Review',
    subText: 'An assessment for new hires at the end of their probation period to determine role fit and next steps.',
    imgSrc: ProbationReviewImage,
  },
  // {
  //   type: 'COMPETENCY_ASSESSMENT',
  //   title: 'Competency-Only Review',
  //   subText: 'A focused review evaluating employees solely on key skills and competencies.',
  //   imgSrc: CompetencyOnlyReviewImage,
  // },
  {
    type: REVIEW_PRESET_TYPES.CUSTOM,
    title: 'Custom Cycle',
    subText: 'A flexible review setup allowing you to customize all elements of the performance review as needed.',
    imgSrc: CustomReviewImage,
  },
];

const ImgTag = ({ label, imgSrc, withImagePadding = false }) => (
  <Flex p="2px" bg="neutral.F2F2F2" gap="4px" borderRadius="4px" alignItems="center">
    <Image src={imgSrc} width={withImagePadding ? '14px' : '16px'} />
    <TextLabelSmall>{label}</TextLabelSmall>
  </Flex>
);

export const ReviewProcessV2Presets = () => {
  const { selectedReviewCreationPreset } = useReviewCreationV1Data();

  const dispatch = useDispatch();
  const isL2NavVisible = useSelectIsL2Visible();

  const handlePresetChange = (selectedPreset) => {
    dispatch(setReviewCreationKey({ selectedReviewCreationPreset: selectedPreset }));
  };

  const getAdditionalTags = (type) => {
    switch (type) {
      case REVIEW_PRESET_TYPES.ANNUAL_APPRAISAL:
        // case REVIEW_PRESET_TYPES.COMPETENCY_ASSESSMENT:
        return (
          <Flex gap={1}>
            <ImgTag label="Mesh recommended" imgSrc={MeshLogoImage} withImagePadding />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.QUARTERLY_REVIEW:
        return (
          <Flex gap={1}>
            {/* <ImgTag label="Recurring - Once every 3 months" imgSrc={RecurringTagImage} /> */}
            <ImgTag label="Mesh recommended" imgSrc={MeshLogoImage} withImagePadding />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.PIP_REVIEW:
        return (
          <Flex gap={1}>
            {/* <ImgTag label="Event - Identified for PIP" imgSrc={EventTypeImage} /> */}
            <ImgTag label="Mesh recommended" imgSrc={MeshLogoImage} withImagePadding />
          </Flex>
        );
      case REVIEW_PRESET_TYPES.PROBATION_REVIEW:
        return (
          <Flex gap={1}>
            <ImgTag label="Event - Post date of joining" imgSrc={EventTypeImage} />
            <ImgTag label="Mesh recommended" imgSrc={MeshLogoImage} withImagePadding />
          </Flex>
        );

      case REVIEW_PRESET_TYPES.CUSTOM:
      default:
        return null;
    }
  };

  return (
    <PaperWithShadow
      display="flex"
      flexDir="column"
      py={0}
      flex={1}
      overflow="auto"
      ml={isL2NavVisible ? '0' : '34px'}
      transition="margin .3s ease;"
    >
      <Flex flex={1} p={4} overflow="auto" borderRadius="8px" bg="white" flexDir="column">
        <TextBodyMedium bold>What type of review do you want to launch?</TextBodyMedium>

        <TextLabel color="text.g2" mb={3}>
          Pick the review type you want to launch and modify the pre-filled form according to your needs in the next
          step.
        </TextLabel>

        <Flex flex={1} p={2} borderRadius="8px" bg="surface.F5F7F9" flexDir="column" gap={2}>
          <Grid gridTemplateColumns="1fr 1fr" gridGap={2}>
            {REVIEW_PRESETS.map((preset) => (
              <RadioCard
                key={preset.type}
                title={preset.title}
                subText={preset.subText}
                imgSrc={preset.imgSrc}
                onClick={() => handlePresetChange(preset.type)}
                checked={selectedReviewCreationPreset === preset.type}
                additionalContent={getAdditionalTags(preset.type)}
              />
            ))}
          </Grid>
        </Flex>
      </Flex>
      <ReviewProcessV2PresetsFooter />
    </PaperWithShadow>
  );
};
